import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import {
  FormInline,
  CheckboxInput
} from '../../../fragments/forms';
import Button from '../../../fragments/button';
import SelectControl from '../../../fragments/select-control';

import API from '../hivdb/api';

import style from './style.css';

import {cloneDeep} from 'lodash';


const DEFAULT_STATE = {
  source: {
    property: 'source',
    value: '',
    options: [],
    prompt: "Change source ...",
  },
  region: {
    property: 'region',
    value: '',
    options: [],
    prompt: "Change region ...",
  },
  isolateDate: {
    property: 'isolateDate',
    value: '',
    startDate: '',
    stopDate: '',
    startDatePlaceholder: '',
    stopDatePlaceholder: '',
    notAvailable: false,
  },
  isolateDateMatch: {
    property: 'isolateDateMatch',
    value: '',
  },
  ptAlias: {
    property: 'ptAlias',
    value: '',
    by_column: 'isolateName',
    updateMethod: 'by_column',
    prompt: "Using isolateName as ptAlias",
    checked: false,
  },
  isChild: {
    property: 'isChild',
    value: '',
    prompt: "All from children",
    checked: false,
  },
  isNGS: {
    property: 'seqMethod',
    value: '',
    prompt: "NGS sequencing used",
    checked: false,
  }
};


export default class UpdatePanel extends React.Component {

  static propTypes = {
    rawDataCollection: PropTypes.object.isRequired,
    onUpdateColumn: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
  };

  constructor() {
    super(...arguments);
    this.state = cloneDeep(DEFAULT_STATE);
  }

  async componentDidMount() {
    this.handleReset();
  }

  handleReset() {
    this.setState(cloneDeep(DEFAULT_STATE));

    this.loadSourceAndRegion();

    const {rawDataCollection} = this.props;
    const {isolateDate} = this.state;

    isolateDate.startDatePlaceholder = rawDataCollection.isolateStartDate;
    isolateDate.stopDatePlaceholder = rawDataCollection.isolateStopDate;
    this.setState({
      isolateDate
    });
  }

  async loadSourceAndRegion() {
    const data = await API({ldInclude: true});
    const {viewer: {
      BodySourceList,
      CountryList}
    } = data;

    let sourceList = BodySourceList.edges.map((node) => node.node.source);
    sourceList = sourceList.filter((source) => {
      return (source !== 'PBMC') && (source !== 'Plasma');
    });
    sourceList.unshift('Plasma');
    sourceList.unshift('PBMC');

    const {source, region} = this.state;
    source.options = sourceList.map((source) => {
      return {
        value: source,
        label: source,
      };
    });

    region.options = CountryList.edges.map((node) => {
      const region = node.node.region;
      return {
        value: region,
        label: region,
      };
    });
    this.setState({
      source,
      region
    });
  }

  handleChooseSource(e) {
    e.preventDefault();

    const {source} = this.state;
    source.value = e.target.value;
    this.setState({source});
  }

  handleChooseRegion(e) {
    e.preventDefault();

    const {region} = this.state;
    region.value = e.target.value;
    this.setState({region});
  }

  handleIsolateStartDate(e) {
    e.preventDefault();

    const {isolateDate} = this.state;
    isolateDate.startDate = e.target.value;
    isolateDate.stopDate = e.target.value;

    isolateDate.value = isolateDate.stopDate;

    this.setState({isolateDate});
    this.handleIsolateDateMatch();
  }

  handleIsolateStopDate(e) {
    e.preventDefault();

    const {isolateDate} = this.state;
    isolateDate.stopDate = e.target.value;

    if (isolateDate.startDate !== '' && isolateDate.stopDate === '') {
      isolateDate.stopDate = isolateDate.startDate;
    }

    isolateDate.value = isolateDate.stopDate;

    this.setState({isolateDate});
    this.handleIsolateDateMatch();
  }

  handleIsolateDateMatch() {
    let {isolateDate, isolateDateMatch} = this.state;
    if (isolateDate.startDate === isolateDate.stopDate) {
      isolateDateMatch.value = '=';
    } else {
      isolateDateMatch.value = '<=';
    }
    this.setState({isolateDateMatch});
  }

  handleIsolateDateNotAvailable(value) {
    const {isolateDate, isolateDateMatch} = this.state;
    isolateDate.notAvailable = value;

    if (isolateDate.notAvailable) {
      if (isolateDate.stopDate === '') {
        isolateDate.value = this.handleGenerateIsolateDate();
        isolateDateMatch.value = '<=';
      } else {
        isolateDate.value = '';
        isolateDateMatch.value = '=';
      }
      this.setState({isolateDate, isolateDateMatch});
    } else {
      isolateDate.value = isolateDate.stopDate;
      this.setState({isolateDate});
      this.handleIsolateDateMatch(isolateDate);
    }
  }

  handleGenerateIsolateDate() {
    const {rawDataCollection: {reference}} = this.props;
    const isolateDate = moment(
      `${reference.refYear}-01-01`).subtract(
      2, 'years').format(
      'YYYY-MM-DD');
    return isolateDate;

  }

  handlePtAlias(value) {
    const {ptAlias} = this.state;
    ptAlias.checked = value;
    if (value) {
      ptAlias.value = ptAlias.by_column;
    } else {
      ptAlias.value = '';
    }
    this.setState({ptAlias});
  }

  handleIsChild(value) {
    const {isChild} = this.state;
    isChild.checked = value;
    if (value) {
      isChild.value = 'Yes';
    } else {
      isChild.value = 'No';
    }
    this.setState({isChild});
  }

  handleIsNGS(value) {
    const {isNGS} = this.state;
    isNGS.checked = value;
    if (value) {
      isNGS.value = 'NGS';
    } else {
      isNGS.value = 'Dideoxy';
    }
    this.setState({isNGS});
  }

  async handleUpdateColumn() {

    const {onUpdateColumn} = this.props;

    for (const column of Object.values(this.state)) {
      if (!column.property) {
        continue;
      }
      if (!column.value) {
        continue;
      }
      const updateMethod = column.updateMethod || 'by_value';
      await onUpdateColumn(updateMethod, column.property, column.value);
    }
    // this.handleReset();
  }

  get ptAliasController() {
    // const {rawDataCollection} = this.props;
    // if (rawDataCollection.reference.loadFrom === 'HIVDB') {
    //   return null;
    // }

    const {
      ptAlias,
    } = this.state;

    return (
      <FormInline dom="div">
        <span className={style.controlGroup}>
          <CheckboxInput
           name="ptAlias"
           checked={ptAlias.checked}
           onChange={
             this.handlePtAlias.bind(this)} >
            {ptAlias.prompt}
          </CheckboxInput>
        </span>
      </FormInline>
    );
  }

  get isNGSContoller() {
    const {rawDataCollection} = this.props;
    if (rawDataCollection.sequenceType === 'incomplete') {
      if (!rawDataCollection.hasCloneSequence) {
        return null;
      }
    }

    const {
      isNGS,
    } = this.state;
    return (
      <FormInline dom="div">
        <span className={style.controlGroup}>
          <CheckboxInput
           name="isNGS"
           checked={isNGS.checked}
           onChange={this.handleIsNGS.bind(this)} >
            {isNGS.prompt}
          </CheckboxInput>
        </span>
      </FormInline>
    );
  }

  render() {
    const {
      source,
      region,
      isolateDate,
      isChild,
    } = this.state;

    const {disabled} = this.props;


    return (
      <div className={style['update-form']}>
        <h3>
          Batch update
        </h3>
        {this.ptAliasController}
        <FormInline dom="div">
          <span className={style.controlGroup}>
            <CheckboxInput
             name="isChild"
             checked={isChild.checked}
             onChange={
               this.handleIsChild.bind(this)} >
              {isChild.prompt}
            </CheckboxInput>
          </span>
        </FormInline>
        {this.isNGSContoller}
        <FormInline dom="div">
          <span className={style.controlGroup}>
            {'Change Sample Year: '}
          </span>
          <span className={style.controlGroup}>
            <input
             value={isolateDate.startDate}
             placeholder={isolateDate.startDatePlaceholder}
             onChange={
              this.handleIsolateStartDate.bind(this)}
              />
          </span>
          <span  className={style.controlGroup}>-</span>
          <span className={style.controlGroup}>
            <input
             value={isolateDate.stopDate}
             placeholder={isolateDate.stopDatePlaceholder}
             onChange={
              this.handleIsolateStopDate.bind(this)}
              />
          </span>
          <span  className={style.controlGroup}>or</span>
          <span className={style.controlGroup}>
            <CheckboxInput
             name="sampleyear"
             checked={isolateDate.notAvailable}
             onChange={this.handleIsolateDateNotAvailable.bind(this)}>
              Not available
            </CheckboxInput>
          </span>
        </FormInline>
        <FormInline dom="div">
          <span className={style.selectGroup}>
            <SelectControl
             options={source.options}
             value={source.value}
             prompt={source.prompt}
             onChange={this.handleChooseSource.bind(this)} />
          </span>
          <span className={style.selectGroup}>
            <SelectControl
             options={region.options}
             value={region.value}
             prompt={region.prompt}
             onChange={
              this.handleChooseRegion.bind(this)} />
          </span>
        </FormInline>
        <FormInline dom="div">
          <Button
           disabled={disabled}
           btnStyle="primary"
           onClick={
            this.handleUpdateColumn.bind(this)}
          >
            Update all
          </Button>
        </FormInline>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/wizard/isolate/gtable_iso/update-panel.js